import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Inventory2,
  Warning,
  Category,
  LocalOffer,
  Refresh,
  TrendingDown,
} from "@mui/icons-material";
import api from "../../services/api";
import MobileDataCard, {
  MobileDataList,
} from "../../components/common/MobileDataCard";
// Colores por categoría
const getCategoriaColor = (categoria) => {
  const colors = {
    Decoración: { bg: "#fce7f3", color: "#be185d" },
    Mobiliario: { bg: "#dbeafe", color: "#1e40af" },
    "Audio/Video": { bg: "#fef3c7", color: "#92400e" },
    Iluminación: { bg: "#fef9c3", color: "#854d0e" },
    Catering: { bg: "#dcfce7", color: "#166534" },
    Papelería: { bg: "#f3e8ff", color: "#7c3aed" },
    Fotografía: { bg: "#cffafe", color: "#0891b2" },
    Otros: { bg: "#f3f4f6", color: "#374151" },
  };
  return colors[categoria] || colors.Otros;
};

// Componente de tarjetas de resumen
const SummaryCards = ({ insumos, alertas }) => {
  const totalInsumos = insumos.length;
  const stockBajo = alertas;
  const valorTotal = insumos.reduce(
    (sum, i) =>
      sum + (parseFloat(i.precio_unitario) || 0) * (parseInt(i.stock) || 0),
    0,
  );

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 3 }}
      justifyContent="center"
      sx={{ mb: 3 }}
    >
      {[
        {
          label: "Total Insumos",
          value: totalInsumos,
          color: "#6B8EFF",
          glow: "rgba(107,142,255,0.35)",
          bg: "radial-gradient(circle at 35% 35%, #1e2d6b, #0d1540)",
          icon: <Inventory2 sx={{ fontSize: { xs: 22, sm: 28 } }} />,
        },
        {
          label: "Stock Bajo",
          value: stockBajo,
          color: stockBajo > 0 ? "#FF6B6B" : "#6BFF9E",
          glow: stockBajo > 0 ? "rgba(255,107,107,0.35)" : "rgba(107,255,158,0.3)",
          bg: stockBajo > 0
            ? "radial-gradient(circle at 35% 35%, #5e1a1a, #2d0b0b)"
            : "radial-gradient(circle at 35% 35%, #1a5e30, #0b2d17)",
          icon: <Warning sx={{ fontSize: { xs: 22, sm: 28 } }} />,
        },
        {
          label: "Valor Total",
          value: `$${valorTotal.toLocaleString("es-MX")}`,
          color: "#6BFF9E",
          glow: "rgba(107,255,158,0.3)",
          bg: "radial-gradient(circle at 35% 35%, #1a5e30, #0b2d17)",
          icon: <LocalOffer sx={{ fontSize: { xs: 22, sm: 28 } }} />,
        },
      ].map((stat, i) => (
        <Grid
          item
          xs={4}
          sm="auto"
          key={i}
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <Box
            sx={{
              width: { xs: 100, sm: 140 },
              height: { xs: 100, sm: 140 },
              borderRadius: "50%",
              background: stat.bg,
              border: `2px solid ${stat.color}50`,
              boxShadow: `0 0 24px ${stat.glow}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.25, sm: 0.5 },
            }}
          >
            <Box sx={{ color: stat.color, lineHeight: 1 }}>{stat.icon}</Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.35rem" },
                color: "#fff",
                lineHeight: 1,
                textAlign: "center",
                px: 0.5,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.55rem", sm: "0.65rem" },
                color: "rgba(255,255,255,0.65)",
                textAlign: "center",
                lineHeight: 1.2,
                px: 1,
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

// Barra visual de nivel de stock
const StockBar = ({ stock, stockMinimo, stockDisponible }) => {
  const actual = parseInt(stock) || 0;
  const minimo = parseInt(stockMinimo) || 1;
  const disponible = parseInt(stockDisponible ?? stock) || 0;
  const pct = Math.min(
    100,
    actual > 0 ? Math.round((disponible / actual) * 100) : 0,
  );
  const isLow = disponible <= minimo;
  return (
    <Box>
      <Typography variant="caption" color={isLow ? "error" : "textSecondary"}>
        {disponible} / {actual}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={isLow ? "error" : "success"}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

const CATEGORIAS = [
  "Todas",
  "Decoración",
  "Mobiliario",
  "Audio/Video",
  "Iluminación",
  "Catering",
  "Papelería",
  "Fotografía",
  "Otros",
];

const INITIAL_FORM = {
  nombre: "",
  descripcion: "",
  categoria: "Otros",
  unidad: "pieza",
  stock: 0,
  stock_minimo: 1,
  precio_unitario: 0,
};

const AdminInsumos = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [insumos, setInsumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("Todas");
  const [showStockBajo, setShowStockBajo] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [saving, setSaving] = useState(false);

  const fetchInsumos = async () => {
    try {
      setLoading(true);
      const res = await api.get("/insumos");
      const data =
        res.data?.data?.insumos ||
        res.data?.insumos ||
        res.data?.data ||
        (Array.isArray(res.data) ? res.data : []);
      setInsumos(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Error al cargar insumos");
      setInsumos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsumos();
  }, []);

  const alertasCount = insumos.filter(
    (i) =>
      (parseInt(i.stock_disponible ?? i.stock) || 0) <=
      (parseInt(i.stock_minimo) || 1),
  ).length;

  const filteredInsumos = insumos.filter((i) => {
    const matchSearch =
      !search || i.nombre?.toLowerCase().includes(search.toLowerCase());
    const matchCat =
      categoriaFilter === "Todas" || i.categoria === categoriaFilter;
    const matchStock =
      !showStockBajo ||
      i.alerta_stock ||
      (parseInt(i.stock_disponible ?? i.stock) || 0) <=
        (parseInt(i.stock_minimo) || 1);
    return matchSearch && matchCat && matchStock;
  });

  const paginatedInsumos = filteredInsumos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleOpenNew = () => {
    setIsEdit(false);
    setForm({ ...INITIAL_FORM });
    setOpenDialog(true);
  };

  const handleOpenEdit = (insumo) => {
    setIsEdit(true);
    setForm({
      id: insumo.id,
      nombre: insumo.nombre || "",
      descripcion: insumo.descripcion || "",
      categoria: insumo.categoria || "Otros",
      unidad: insumo.unidad || "pieza",
      stock: insumo.stock || 0,
      stock_minimo: insumo.stock_minimo || 1,
      precio_unitario: insumo.precio_unitario || 0,
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setForm({ ...INITIAL_FORM });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este insumo?")) return;
    try {
      await api.delete(`/insumos/${id}`);
      setSuccess("Insumo eliminado correctamente");
      fetchInsumos();
    } catch (err) {
      setError(err.response?.data?.message || "Error al eliminar");
    }
  };

  const handleSave = async () => {
    if (!form.nombre?.trim()) {
      setError("El nombre es requerido");
      return;
    }
    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/insumos/${form.id}`, form);
        setSuccess("Insumo actualizado correctamente");
      } else {
        await api.post("/insumos", form);
        setSuccess("Insumo creado correctamente");
      }
      handleClose();
      fetchInsumos();
    } catch (err) {
      setError(err.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={{ xs: 2, sm: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.25rem", sm: "2.125rem" } }}
          >
            📦 Gestión de Insumos
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            Inventario y control de materiales para eventos
          </Typography>
        </Box>
        {/* Botones desktop */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchInsumos} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenNew}
            sx={{ borderRadius: 2 }}
          >
            Nuevo Insumo
          </Button>
        </Box>
        {/* Botones móvil */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 1 }}>
          <IconButton onClick={fetchInsumos} color="primary">
            <Refresh />
          </IconButton>
          <IconButton
            onClick={handleOpenNew}
            sx={{
              width: 44,
              height: 44,
              background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
              color: "#fff",
              boxShadow: "0 3px 8px rgba(25, 118, 210, .4)",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
              },
            }}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <SummaryCards insumos={insumos} alertas={alertasCount} />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar insumos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={categoriaFilter}
                label="Categoría"
                onChange={(e) => setCategoriaFilter(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <Category fontSize="small" />
                  </InputAdornment>
                }
              >
                {CATEGORIAS.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant={showStockBajo ? "contained" : "outlined"}
              color={showStockBajo ? "error" : "inherit"}
              startIcon={<TrendingDown />}
              onClick={() => setShowStockBajo(!showStockBajo)}
              fullWidth
            >
              <Badge badgeContent={alertasCount} color="error" sx={{ mr: 1 }}>
                Stock Bajo
              </Badge>
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
            >
              {filteredInsumos.length} resultado(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Vista Móvil */}
      {isMobile ? (
        <Box>
          <MobileDataList
            loading={loading}
            emptyMessage="No hay insumos registrados"
          >
            {paginatedInsumos.map((insumo) => {
              const catColor = getCategoriaColor(insumo.categoria);
              return (
                <MobileDataCard
                  key={insumo.id}
                  title={insumo.nombre || "Sin nombre"}
                  subtitle={insumo.categoria || "Sin categoría"}
                  avatarColor={catColor.color}
                  status={{
                    label: insumo.alerta_stock ? "Stock bajo" : "OK",
                    color: insumo.alerta_stock ? "#f44336" : "#4caf50",
                  }}
                  primaryInfo={[
                    {
                      icon: <Inventory2 fontSize="small" />,
                      label: "Stock",
                      value: `${insumo.stock_disponible || insumo.stock || 0} / ${insumo.stock || 0}`,
                    },
                    {
                      icon: <LocalOffer fontSize="small" />,
                      label: "Precio",
                      value: `$${parseFloat(insumo.precio_unitario || 0).toLocaleString()}`,
                    },
                  ]}
                  expandedInfo={[
                    {
                      icon: <Category fontSize="small" />,
                      label: "Categoría",
                      value: insumo.categoria || "Otros",
                    },
                    {
                      icon: <Warning fontSize="small" />,
                      label: "Stock mínimo",
                      value: insumo.stock_minimo || 1,
                    },
                    {
                      icon: <Inventory2 fontSize="small" />,
                      label: "Unidad",
                      value: insumo.unidad || "pieza",
                    },
                  ]}
                  actions={[
                    {
                      icon: <Edit fontSize="small" />,
                      label: "Editar",
                      onClick: () => handleOpenEdit(insumo),
                    },
                    {
                      icon: <Delete fontSize="small" />,
                      label: "Eliminar",
                      color: "error",
                      onClick: () => handleDelete(insumo.id),
                    },
                  ]}
                />
              );
            })}
          </MobileDataList>
          <TablePagination
            component="div"
            count={filteredInsumos.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Box>
      ) : (
        /* Vista Desktop - Table */
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1e293b" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  #
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Nombre
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Categoría
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Stock
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Unidad
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Precio Unit.
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold" }}
                  align="center"
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInsumos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Inventory2
                      sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }}
                    />
                    <Typography color="textSecondary">
                      No hay insumos
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInsumos.map((insumo, index) => {
                  const catColor = getCategoriaColor(insumo.categoria);
                  return (
                    <TableRow
                      key={insumo.id}
                      hover
                      sx={{
                        backgroundColor: insumo.alerta_stock
                          ? "rgba(239, 68, 68, 0.1)"
                          : "inherit",
                        "&:hover": {
                          backgroundColor: insumo.alerta_stock
                            ? "rgba(239, 68, 68, 0.15)"
                            : "#f8fafc",
                        },
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography fontWeight="medium">
                            {insumo.nombre}
                          </Typography>
                          {insumo.descripcion && (
                            <Typography variant="caption" color="textSecondary">
                              {insumo.descripcion.substring(0, 50)}
                              {insumo.descripcion.length > 50 ? "..." : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={insumo.categoria || "Otros"}
                          size="small"
                          sx={{
                            backgroundColor: catColor.bg,
                            color: catColor.color,
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <StockBar
                          stock={insumo.stock}
                          stockMinimo={insumo.stock_minimo}
                          stockDisponible={insumo.stock_disponible}
                        />
                      </TableCell>
                      <TableCell>{insumo.unidad || "pieza"}</TableCell>
                      <TableCell>
                        $
                        {parseFloat(
                          insumo.precio_unitario || 0,
                        ).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEdit(insumo)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(insumo.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredInsumos.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </TableContainer>
      )}

      {/* Dialog Create/Edit */}
      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1e293b", color: "white" }}>
          {isEdit ? "✏️ Editar Insumo" : "➕ Nuevo Insumo"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre *"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                error={!form.nombre.trim() && saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={form.descripcion}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select
                  name="categoria"
                  value={form.categoria}
                  label="Categoría"
                  onChange={handleChange}
                >
                  {CATEGORIAS.filter((c) => c !== "Todas").map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unidad"
                name="unidad"
                value={form.unidad}
                onChange={handleChange}
                placeholder="pieza, set, metro..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock"
                name="stock"
                type="number"
                value={form.stock}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock Mínimo"
                name="stock_minimo"
                type="number"
                value={form.stock_minimo}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Precio Unitario"
                name="precio_unitario"
                type="number"
                value={form.precio_unitario}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? "Guardando..." : isEdit ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminInsumos;
